// One-shot "return to the post you were creating" hint, used when a user leaves
// the post editor to connect an account (a full-page OAuth redirect) and lands
// on /settings. Task-specific connections preserve the action until the editor
// resumes; ordinary editor connections offer the existing Continue your post link.

const KEY = "wavegen:returnToPost";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 min — guards against abandoned OAuth

// Record the post to return to. Overwrites any previous value, so the most
// recent connect always wins.
export function stashReturnToPost(draftId: string, intent?: string): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ draftId, intent, ts: Date.now() }));
  } catch {
    // sessionStorage unavailable (private mode, quota) — non-fatal.
  }
}

// Reading is non-destructive while OAuth is in flight. Consume on task resume.
// Returns null if absent, malformed, or older than MAX_AGE_MS.
export function readReturnToPostTask(consume = false): { draftId: string; intent?: string } | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (consume) sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const { draftId, ts, intent } = JSON.parse(raw) as {
      draftId?: string;
      ts?: number;
      intent?: string;
    };
    if (typeof draftId !== "string" || !draftId || typeof ts !== "number") return null;
    if (Date.now() - ts > MAX_AGE_MS) return null;
    return { draftId, ...(typeof intent === "string" ? { intent } : {}) };
  } catch {
    return null;
  }
}

export function consumeReturnToPost(): string | null {
  return readReturnToPostTask(true)?.draftId || null;
}
